'use es6';

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { OrderedMap } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIMoveableObject from 'UIComponents/draggable/UIMoveableObject';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.form.withInlineEditing.${key}`;
const FormField = ({
  label = '',
  isDeletable = true,
  draggable = false,
  hasFormsWriteAccess = true,
  dragHandle = () => {},
  onEditClick = () => {},
  onRemoveClick = () => {},
  readOnly = false
}) => /*#__PURE__*/_jsxs(UIFlex, {
  "data-selenium": "form-field",
  children: [/*#__PURE__*/_jsx(UIBox, {
    grow: 1,
    children: /*#__PURE__*/_jsx(UIMoveableObject, {
      closeable: false,
      draggable: draggable,
      dragHandle: dragHandle,
      children: label
    })
  }), /*#__PURE__*/_jsxs(UIBox, {
    shrink: 0,
    className: "p-left-3",
    children: [hasFormsWriteAccess && /*#__PURE__*/_jsx(UIIconButton, {
      "data-selenium": "form-field-edit-button",
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('formField.edit')
      }),
      placement: "top",
      onClick: onEditClick,
      disabled: readOnly,
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: "edit"
      })
    }), isDeletable && hasFormsWriteAccess && /*#__PURE__*/_jsx(UIIconButton, {
      "data-selenium": "form-field-remove-button",
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('formField.delete')
      }),
      placement: "top",
      onClick: onRemoveClick,
      disabled: readOnly,
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: "delete"
      })
    })]
  })]
});
const FormFieldsWithoutReordering = ({
  fields = [],
  hasFormsWriteAccess = false,
  onEditClick = () => {},
  onRemoveClick = () => {},
  readOnly = false
}) => /*#__PURE__*/_jsx("div", {
  children: fields.map(({
    field
  }, key) => /*#__PURE__*/_jsx(View, {
    marginBlockEnd: "200",
    as: "div",
    children: /*#__PURE__*/_jsx(FormField, {
      label: field.label || field.placeholder,
      draggable: false,
      isDeletable: field.name !== 'email',
      onEditClick: () => onEditClick(key),
      onRemoveClick: () => onRemoveClick(key),
      hasFormsWriteAccess: hasFormsWriteAccess,
      readOnly: readOnly
    }, key)
  }, key)).valueSeq().toArray()
});
const FormFieldsWithReordering = props => {
  const {
    fields = [],
    onOrderChange = () => {},
    onEditClick = () => {},
    onRemoveClick = () => {},
    DraggableComponents
  } = props;
  const {
    UISortableList,
    UIDragListItem,
    UIDragHandle
  } = DraggableComponents;
  const [_order, setFieldsOrder] = useState([...fields.keys()]);
  const [_fields, setFormFields] = useState(fields);
  useEffect(() => {
    setFormFields(props.fields);
    if (_order.length !== props.fields.size) {
      setFieldsOrder([...props.fields.keys()]);
    }
  }, [_order.length, props.fields]);
  const updateOrder = updatedOrder => {
    setFieldsOrder(updatedOrder);
    onOrderChange(updatedOrder);
  };
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(UISortableList, {
      dragGroup: "unique-key",
      order: _order,
      onOrderChange: updateOrder,
      children: _fields.map(({
        field
      }, key) => /*#__PURE__*/_jsx(UIDragListItem, {
        dragId: key,
        children: /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(FormField, {
            label: field.label || field.placeholder,
            draggable: true,
            dragHandle: UIDragHandle,
            isDeletable: field.name !== 'email',
            onEditClick: () => onEditClick(key),
            onRemoveClick: () => onRemoveClick(key)
          }, key)
        })
      }, key)).valueSeq().toArray()
    })
  });
};
const getFormFieldsComponent = ({
  canChangeFieldsOrder,
  DraggableComponents,
  hasFormsWriteAccess,
  readOnly
}) => {
  return !readOnly && canChangeFieldsOrder && DraggableComponents && DraggableComponents.UISortableList && DraggableComponents.UIDragListItem && DraggableComponents.UIDragHandle && hasFormsWriteAccess ? FormFieldsWithReordering : FormFieldsWithoutReordering;
};
const propTypes = {
  fields: PropTypes.instanceOf(OrderedMap),
  onEditClick: PropTypes.func,
  onRemoveClick: PropTypes.func
};
FormFieldsWithReordering.propTypes = Object.assign({}, propTypes, {
  DraggableComponents: PropTypes.object.isRequired,
  onOrderChange: PropTypes.func
});
FormFieldsWithoutReordering.propTypes = Object.assign({}, propTypes, {
  hasFormsWriteAccess: PropTypes.bool
});
export default getFormFieldsComponent;